/**
 * Auth store — manages the Cognito OIDC session and exposes the current user.
 *
 * Flow:
 *   - On bootstrap, restore the session from sessionStorage (set by the OIDC
 *     client after a successful auth/callback).
 *   - signIn() kicks off the Cognito hosted-UI redirect.
 *   - signOut() clears local state and redirects to Cognito logout.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts'
import { decodeCovers, type Role, type UserContext } from '@medlive-hris/shared-types'
import { useCalibrationExpandState } from '@/composables/useCalibrationExpandState'

// ── Auth return-path handoff ──────────────────────────────────────
//
// When we bounce the user through Cognito's hosted UI (either from
// the login page, or from an interactive re-auth triggered by an
// expired session), we want them to land back on the page they were
// on. `oidc.signinRedirect` doesn't natively echo state, so we
// stash the return path in sessionStorage before the redirect and
// pop it back off in the auth callback. sessionStorage (not local)
// so this doesn't survive a full browser restart.
const AUTH_RETURN_PATH_KEY = 'medlive-hris.auth.returnPath'
function stashReturnPath(path: string): void {
  try { window.sessionStorage.setItem(AUTH_RETURN_PATH_KEY, path) } catch { /* private mode */ }
}
export function consumeAuthReturnPath(): string | null {
  try {
    const p = window.sessionStorage.getItem(AUTH_RETURN_PATH_KEY)
    if (p) window.sessionStorage.removeItem(AUTH_RETURN_PATH_KEY)
    return p
  } catch { return null }
}

// ── Impersonation state persistence ──────────────────────────────
// Stored separately from the OIDC session because we want it to survive a
// reload but NOT a sign-out. The auth store's signOut() clears this on its
// way out.
const IMPERSONATION_KEY = 'medlive-hris.impersonation'
interface Impersonation {
  empId: string
  displayName: string
  roles: Role[]
  // hasReports replaces the legacy 'manager' role check. PeoplePage derives
  // this from the target employee row (live Graph call also runs to refresh
  // roles, but hasReports is read straight from the row's hasReports flag).
  hasReports: boolean
}
function readImpersonation(): Impersonation | null {
  try {
    const raw = window.localStorage.getItem(IMPERSONATION_KEY)
    return raw ? JSON.parse(raw) as Impersonation : null
  } catch {
    return null
  }
}
function writeImpersonation(v: Impersonation | null): void {
  if (v) window.localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(v))
  else window.localStorage.removeItem(IMPERSONATION_KEY)
}

const oidc = new UserManager({
  authority: `https://cognito-idp.us-east-1.amazonaws.com/${import.meta.env.VITE_USER_POOL_ID}`,
  client_id: import.meta.env.VITE_USER_POOL_CLIENT_ID,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: `${window.location.origin}/`,
  response_type: 'code',
  scope: 'openid profile email',
  metadata: {
    // Override the OIDC discovery endpoints to point at the Cognito hosted UI
    issuer: `https://cognito-idp.us-east-1.amazonaws.com/${import.meta.env.VITE_USER_POOL_ID}`,
    authorization_endpoint: `https://${import.meta.env.VITE_COGNITO_DOMAIN}/oauth2/authorize`,
    token_endpoint: `https://${import.meta.env.VITE_COGNITO_DOMAIN}/oauth2/token`,
    userinfo_endpoint: `https://${import.meta.env.VITE_COGNITO_DOMAIN}/oauth2/userInfo`,
    end_session_endpoint: `https://${import.meta.env.VITE_COGNITO_DOMAIN}/logout?client_id=${import.meta.env.VITE_USER_POOL_CLIENT_ID}&logout_uri=${window.location.origin}/`,
  },
  // localStorage so the session survives page refresh, tab close, and browser
  // restart. Access token validity is 1h (set in the Auth CDK stack) and the
  // refresh token is 30d, so automaticSilentRenew keeps things fresh in the
  // background. To force a fresh login, the user must hit Sign out.
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: true,
})

export const useAuthStore = defineStore('auth', () => {
  const oidcUser = ref<User | null>(null)
  const user = ref<UserContext | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Impersonation: rehydrate from localStorage on store init so a page
  // refresh during a "view as" session keeps the impersonation alive.
  const impersonation = ref<Impersonation | null>(readImpersonation())
  const isImpersonating = computed(() => !!impersonation.value)
  const impersonatingDisplayName = computed(() => impersonation.value?.displayName ?? null)
  const impersonationEmpId = computed(() => impersonation.value?.empId ?? null)
  const impersonationRoles = computed(() => impersonation.value?.roles ?? [])
  const impersonationHasReports = computed(() => !!impersonation.value?.hasReports)

  // Effective hasReports — the target's value while impersonating, the real
  // caller's value otherwise. UI gates that used to check hasRole('manager')
  // now check this instead.
  const hasReports = computed(() => {
    if (impersonation.value) return impersonation.value.hasReports
    return !!user.value?.hasReports
  })

  // Effective empId — the target's id when impersonating, otherwise the
  // real caller's id. Use this for any "is this the user named on the
  // row?" check (manager-of-record, employee-of-record, etc). Reading
  // `auth.user?.empId` directly leaves those gates broken during HR
  // impersonation because the real caller is always HR.
  const effectiveEmpId = computed(() => {
    if (impersonation.value) return impersonation.value.empId
    return user.value?.empId ?? null
  })

  // The "real" caller (always the HR user holding the JWT). hasRole() below
  // honors impersonation, but UI that needs the actual identity (the banner,
  // the People page Impersonate button gating) reads `realUser`.
  const realUser = computed(() => user.value)

  const isLoggedIn = computed(() => !!oidcUser.value && !oidcUser.value.expired)
  const accessToken = computed(() => oidcUser.value?.access_token ?? null)
  const idToken = computed(() => oidcUser.value?.id_token ?? null)

  function startImpersonating(empId: string, displayName: string, roles: Role[], hasReports: boolean): void {
    if (!user.value?.roles.includes('hr')) {
      console.warn('[auth] non-HR user tried to impersonate; ignored')
      return
    }
    const next: Impersonation = { empId, displayName, roles, hasReports }
    impersonation.value = next
    writeImpersonation(next)
    // Hard reload so every fetch in flight redraws with the new context.
    window.location.assign('/home')
  }
  function stopImpersonating(): void {
    impersonation.value = null
    writeImpersonation(null)
    window.location.assign('/home')
  }

  // Memoize the bootstrap promise so the router guard can `await` it without
  // triggering N parallel `oidc.getUser()` calls per navigation. The OIDC
  // client also fires `userLoaded`/`userUnloaded` events after silent renew —
  // wire those up so the store stays current as the access token rotates.
  let bootstrapPromise: Promise<void> | null = null
  async function bootstrap(): Promise<void> {
    if (bootstrapPromise) return bootstrapPromise
    bootstrapPromise = (async () => {
      loading.value = true
      try {
        const u = await oidc.getUser()
        if (u && !u.expired) {
          oidcUser.value = u
          user.value = claimsToUserContext(u)
        }
      } catch (e) {
        console.error('[auth] bootstrap error', e)
      } finally {
        loading.value = false
      }
    })()
    return bootstrapPromise
  }

  oidc.events.addUserLoaded((u) => {
    oidcUser.value = u
    user.value = claimsToUserContext(u)
  })
  oidc.events.addUserUnloaded(() => {
    oidcUser.value = null
    user.value = null
  })
  oidc.events.addAccessTokenExpired(() => {
    // The library's silent renewal failed. Instead of just clearing
    // state (which leaves the user staring at a page that quietly
    // stops working), proactively hand them off to Cognito with the
    // current URL preserved. If they still have a live Microsoft
    // SSO session upstream, the hosted UI round-trip is a blink and
    // they're back where they were.
    oidcUser.value = null
    user.value = null
    // Only redirect if we're actually on an app page. During the
    // OIDC callback the library's internal flow also fires this
    // event once — redirecting from there causes an infinite loop.
    const path = window.location.pathname
    if (path === '/auth/callback' || path === '/login') return
    void ensureFreshSession()
  })

  // Prevents concurrent /me-esque callers from all firing their
  // own signinRedirect the moment their token expires — only the
  // first one wins; the rest await the same promise.
  let renewalPromise: Promise<boolean> | null = null

  /**
   * Make sure the store has a non-expired access token. If the
   * current token is missing or expired, first try oidc-client-ts's
   * silent renewal (hidden iframe → Cognito's authorize endpoint
   * with prompt=none). If that fails — refresh token has aged out,
   * 3rd-party cookies are blocked, upstream session is gone — kick
   * off a full `signinRedirect` and preserve the caller's current
   * URL so they land back on the same page after re-authenticating.
   *
   * Resolves `true` when a live session is in hand and the caller
   * can safely fire its API request. Resolves `false` only when a
   * full redirect was queued (the browser is navigating away, so
   * the caller should treat its work as abandoned).
   */
  async function ensureFreshSession(returnPath?: string): Promise<boolean> {
    if (renewalPromise) return renewalPromise
    renewalPromise = (async () => {
      try {
        // Check the on-disk state directly — the in-memory
        // `oidcUser` ref can lag because `expired` is set by the
        // library's own timer, not our watcher.
        const current = await oidc.getUser()
        if (current && !current.expired) {
          if (oidcUser.value?.access_token !== current.access_token) {
            oidcUser.value = current
            user.value = claimsToUserContext(current)
          }
          return true
        }
        try {
          const renewed = await oidc.signinSilent()
          if (renewed && !renewed.expired) {
            oidcUser.value = renewed
            user.value = claimsToUserContext(renewed)
            return true
          }
        } catch (e) {
          console.warn('[auth] silent renewal failed; falling back to full redirect', e)
        }
        // Interactive re-auth. Callers that know the intended
        // destination (e.g. router guards firing during a nav to
        // /people) pass returnPath so post-login lands there, not
        // on whatever page the user's browser was showing at the
        // moment of failure.
        await signIn(returnPath)
        return false
      } finally {
        // Reset so the next expiry can trigger a fresh attempt.
        renewalPromise = null
      }
    })()
    return renewalPromise
  }

  /**
   * Force a silent token renewal (no redirect). Used when /me shows the
   * delegation coverage changed since this token was issued - the API
   * trusts the token's `custom:covers` claim, so without a refresh the
   * delegate would see "Covering for …" sections but get 403s.
   */
  async function refreshTokenSilently(): Promise<boolean> {
    try {
      const renewed = await oidc.signinSilent()
      if (renewed && !renewed.expired) { oidcUser.value = renewed; user.value = claimsToUserContext(renewed); return true }
    } catch (e) { console.warn('[auth] silent refresh failed', e) }
    return false
  }

  async function signIn(returnPath?: string) {
    // Default to the caller's current URL. If they explicitly want
    // to send the user somewhere else after login (e.g. router
    // guard redirecting from a role-gated page), pass that path.
    const target = returnPath ?? (window.location.pathname + window.location.search + window.location.hash)
    if (target && target !== '/login' && target !== '/auth/callback') {
      stashReturnPath(target)
    }
    await oidc.signinRedirect()
  }

  async function completeSignIn() {
    const u = await oidc.signinRedirectCallback()
    oidcUser.value = u
    user.value = claimsToUserContext(u)
  }

  async function signOut() {
    oidcUser.value = null
    user.value = null
    // Sign-out should always end any active impersonation. Otherwise the
    // next user to log in on this device could inherit it.
    writeImpersonation(null)
    impersonation.value = null
    // Clear any UI state we persisted across the SPA that shouldn't
    // carry between users on the same device. Add new resets here as
    // more composables grow persisted state.
    useCalibrationExpandState().clearAll()
    await oidc.signoutRedirect()
  }

  /**
   * hasRole honors impersonation: while "viewing as" Jane the manager,
   * hasRole('hr') returns false even though the real caller is HR. This
   * keeps the nav rendering Jane's sections, not HR's.
   */
  function hasRole(role: string): boolean {
    if (impersonation.value) return impersonation.value.roles.includes(role as Role)
    // HR (and Finance) powers only on HR screens (see admin/hrContext.ts).
    if ((role === 'hr' || role === 'finance') && !hrContext.value) return false
    return !!user.value?.roles?.includes(role as never)
  }

  /**
   * Is the current screen an HR screen? Set by the router on every
   * navigation. Off on "my work" screens (My team, Roles, manager /
   * employee / exec reviews) so HR users act as their org role there.
   */
  const hrContext = ref(true)
  function setHrContext(on: boolean): void { hrContext.value = on }
  /**
   * Holds the HR role at all, regardless of the current screen (still
   * honoring impersonation). For entry points into HR screens - the
   * Admin gear, the role label - not for gating HR actions.
   */
  const isHrUser = computed(() => impersonation.value
    ? impersonation.value.roles.includes('hr')
    : !!user.value?.roles?.includes('hr' as never))
  /** Holds the Finance role (Compensation data / export). Same caveats as isHrUser. */
  const isFinanceUser = computed(() => impersonation.value
    ? impersonation.value.roles.includes('finance')
    : !!user.value?.roles?.includes('finance' as never))
  /** May open the Admin gear at all: HR sees everything, Finance sees Compensation. */
  const isAdminUser = computed(() => isHrUser.value || isFinanceUser.value)

  /**
   * Patch the effective `hasReports` value with live truth from the
   * server. AppLayout calls this after fetching /me so the flag
   * matches DDB truth on every page load — no waiting for Cognito
   * to rotate the access token.
   *
   * During impersonation we ALSO overwrite the impersonation record's
   * hasReports. Otherwise a stale value stuffed in at Impersonate-
   * click time (from a still-drifted Employee.hasReports on the HR
   * PeoplePage read) sticks even after the backfill catches up in
   * DDB — /me would report the fresh value but the auth store would
   * still return the stale impersonation snapshot from
   * `hasReports.value`.
   */
  function applyLiveHasReports(next: boolean): void {
    if (impersonation.value) {
      if (impersonation.value.hasReports === next) return
      const nextRecord: Impersonation = { ...impersonation.value, hasReports: next }
      impersonation.value = nextRecord
      writeImpersonation(nextRecord)
      return
    }
    if (!user.value) return
    if (user.value.hasReports === next) return
    user.value = { ...user.value, hasReports: next }
  }

  return {
    user, oidcUser, loading, error, isLoggedIn, accessToken, idToken,
    bootstrap, signIn, signOut, completeSignIn, hasRole, hasReports,
    hrContext, setHrContext, isHrUser, isFinanceUser, isAdminUser,
    effectiveEmpId, applyLiveHasReports, ensureFreshSession, refreshTokenSilently,
    // Impersonation API
    isImpersonating, impersonatingDisplayName, impersonationEmpId,
    impersonationRoles, impersonationHasReports,
    realUser, startImpersonating, stopImpersonating,
  }
})

function claimsToUserContext(u: User): UserContext {
  const p = u.profile as Record<string, unknown>
  // Compose displayName from whatever's available, in priority order:
  //   1. explicit `name` claim (Cognito sometimes provides)
  //   2. given + family (the most reliable since we map those from Entra)
  //   3. email local-part
  //   4. literal 'You' as the last resort
  const given = (p['given_name'] as string) ?? ''
  const family = (p['family_name'] as string) ?? ''
  const email = (p['email'] as string) ?? ''
  const composed = [given, family].filter(Boolean).join(' ').trim()
  const fallback = email.split('@')[0]
  const displayName = (p['name'] as string)
    || composed
    || fallback
    || 'You'
  return {
    empId: (p['custom:emp_id'] as string) ?? '',
    entraOid: (p['custom:entra_oid'] as string) ?? '',
    email,
    displayName,
    managerId: ((p['custom:manager_id'] as string) || null),
    department: (p['custom:dept'] as string) ?? '',
    roles: ((p['custom:roles'] as string) ?? 'employee').split(',').filter(Boolean) as UserContext['roles'],
    hasReports: ((p['custom:has_reports'] as string) ?? 'false') === 'true',
    covers: decodeCovers((p['custom:covers'] as string) ?? ''),
  }
}
